import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useSettings } from "@/contexts/SettingsContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  Upload,
  Loader2,
  X,
  AlertCircle,
  CheckCircle2,
  Users,
  UserPlus,
  Search,
  Ticket,
  Calendar,
  MapPin,
  Clock,
  ClipboardList,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RosterTab({ cohortId, cohortStatus }) {
  const { settings } = useSettings();
  const label = settings.learner_label || "Learner";
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  // Historical orphan discovery modal state
  const [showHistorical, setShowHistorical] = useState(false);
  const [historicalOrphans, setHistoricalOrphans] = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [historicalSelected, setHistoricalSelected] = useState(() => new Set());
  const [historicalEnrolling, setHistoricalEnrolling] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ first_name: "", last_name: "", email: "" });
  const [addError, setAddError] = useState("");
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const fileRef = useRef(null);

  // "Add Learner" modal mode: "search" (default — pick from learner mgmt DB)
  // or "create" (fall back to creating a brand-new learner record).
  const [addMode, setAddMode] = useState("search");
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [enrollingId, setEnrollingId] = useState(null);

  // ── Bulk-select + passport assignment ────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignOptions, setAssignOptions] = useState([]);
  const [assignOptionsLoading, setAssignOptionsLoading] = useState(false);
  // assignDrafts: { [event_id]: { checked: bool, passport_id: string|null } }
  // Each row's checkbox toggles `checked`; the passport radio selects passport_id.
  // On save we diff against the initial state for the single-learner case
  // (so unchecking an already-assigned event = unassign).
  const [assignDrafts, setAssignDrafts] = useState({});
  const [initialAssignDrafts, setInitialAssignDrafts] = useState({});
  const [assigning, setAssigning] = useState(false);

  const toggleLearnerSelected = (lid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(lid)) next.delete(lid); else next.add(lid);
      return next;
    });
  };

  const allSelected = learners.length > 0 && learners.every((c) => selectedIds.has(c.id));
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(learners.map((c) => c.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const openAssignModal = async () => {
    setAssignOpen(true);
    setAssignOptionsLoading(true);
    try {
      const { data } = await axios.get(`${API}/passport-options`, { withCredentials: true });
      const evts = data?.events || [];
      setAssignOptions(evts);
      // Single-learner mode: seed checkboxes from that learner's existing
      // passport_assignments. Multi-learner: start everything unchecked.
      const seed = {};
      if (selectedIds.size === 1) {
        const onlyId = Array.from(selectedIds)[0];
        const learner = learners.find((c) => c.id === onlyId);
        const existing = learner?.passport_assignments || [];
        evts.forEach((ev) => {
          const match = existing.find((a) => a.event_id === ev.event_id);
          seed[ev.event_id] = match
            ? { checked: true, passport_id: match.passport_id }
            : { checked: false, passport_id: null };
        });
      } else {
        evts.forEach((ev) => { seed[ev.event_id] = { checked: false, passport_id: null }; });
      }
      setAssignDrafts(seed);
      setInitialAssignDrafts(JSON.parse(JSON.stringify(seed)));
    } catch {
      toast.error("Failed to load passports");
      setAssignOptions([]);
    } finally { setAssignOptionsLoading(false); }
  };

  const toggleAssignEvent = (eventId) => {
    setAssignDrafts((prev) => {
      const cur = prev[eventId] || { checked: false, passport_id: null };
      // Toggling on: auto-select the event's first passport if none picked.
      if (!cur.checked) {
        const ev = assignOptions.find((e) => e.event_id === eventId);
        const firstPid = ev?.passports?.[0]?.id || null;
        return { ...prev, [eventId]: { checked: true, passport_id: cur.passport_id || firstPid } };
      }
      return { ...prev, [eventId]: { checked: false, passport_id: null } };
    });
  };

  const pickAssignPassport = (eventId, passportId) => {
    setAssignDrafts((prev) => ({
      ...prev,
      [eventId]: { checked: true, passport_id: passportId },
    }));
  };

  const handleAssign = async () => {
    // Build ops to apply: assign for every checked event w/ a passport,
    // unassign for events whose checkbox went from checked→unchecked
    // (single-learner mode only; multi-learner mode doesn't have a notion
    // of "initially assigned" because each learner may differ).
    const learnerIds = Array.from(selectedIds);
    if (learnerIds.length === 0) return;
    const assignOps = [];
    const unassignOps = [];
    Object.entries(assignDrafts).forEach(([eventId, state]) => {
      const initial = initialAssignDrafts[eventId] || { checked: false, passport_id: null };
      if (state.checked) {
        if (!state.passport_id) {
          // user checked but didn't pick a passport — surfaces as error below
          assignOps.push({ eventId, passportId: null, invalid: true });
        } else if (
          state.passport_id !== initial.passport_id || !initial.checked || learnerIds.length > 1
        ) {
          assignOps.push({ eventId, passportId: state.passport_id });
        }
      } else if (initial.checked && learnerIds.length === 1) {
        unassignOps.push({ eventId });
      }
    });
    const invalid = assignOps.find((op) => op.invalid);
    if (invalid) {
      const ev = assignOptions.find((e) => e.event_id === invalid.eventId);
      toast.error(`Select a passport for "${ev?.event_name || "the event"}"`);
      return;
    }
    if (assignOps.length === 0 && unassignOps.length === 0) {
      toast.info ? toast.info("No changes to save") : toast.success("No changes to save");
      setAssignOpen(false);
      return;
    }
    setAssigning(true);
    let assignedCount = 0;
    let replacedCount = 0;
    let unassignedCount = 0;
    try {
      for (const op of assignOps) {
        const { data } = await axios.post(
          `${API}/passports/${op.passportId}/assign`,
          { learner_ids: learnerIds, passport_id: op.passportId },
          { withCredentials: true },
        );
        assignedCount += data?.assigned || 0;
        replacedCount += data?.replaced || 0;
      }
      for (const op of unassignOps) {
        const { data } = await axios.post(
          `${API}/events/${op.eventId}/unassign`,
          { learner_ids: learnerIds, passport_id: "" },
          { withCredentials: true },
        );
        unassignedCount += data?.unassigned || 0;
      }
      const parts = [];
      if (assignedCount > 0) parts.push(`${assignedCount} assigned`);
      if (replacedCount > 0) parts.push(`${replacedCount} replaced`);
      if (unassignedCount > 0) parts.push(`${unassignedCount} unassigned`);
      toast.success(parts.length ? parts.join(", ") : "Saved");
      setAssignOpen(false);
      clearSelection();
      fetchRoster();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save assignments");
    } finally { setAssigning(false); }
  };

  const fetchRoster = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cohorts/${cohortId}/learners`, {
        withCredentials: true,
      });
      setLearners(data);
    } catch {
      toast.error("Failed to load roster");
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  const openHistoricalOrphans = async () => {
    setShowHistorical(true);
    setHistoricalLoading(true);
    setHistoricalSelected(new Set());
    try {
      const { data } = await axios.get(
        `${API}/cohorts/${cohortId}/historical-orphans`,
        { withCredentials: true },
      );
      const list = Array.isArray(data) ? data : [];
      setHistoricalOrphans(list);
      // Pre-select all by default so admins can enroll everyone with one click
      setHistoricalSelected(new Set(list.map((o) => o.email)));
    } catch {
      toast.error("Failed to load historical orphans");
      setHistoricalOrphans([]);
    } finally { setHistoricalLoading(false); }
  };

  const toggleOrphan = (email) => {
    setHistoricalSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };
  const toggleAllOrphans = () => {
    if (historicalSelected.size === historicalOrphans.length) {
      setHistoricalSelected(new Set());
    } else {
      setHistoricalSelected(new Set(historicalOrphans.map((o) => o.email)));
    }
  };

  const confirmEnrollOrphans = async () => {
    const chosen = historicalOrphans.filter((o) => historicalSelected.has(o.email));
    if (chosen.length === 0) { toast.error("Select at least one learner"); return; }
    setHistoricalEnrolling(true);
    try {
      const { data } = await axios.post(
        `${API}/cohorts/${cohortId}/learners/bulk-enroll`,
        {
          learners: chosen.map((o) => ({
            first_name: o.first_name || "",
            last_name: o.last_name || "",
            email: o.email,
          })),
        },
        { withCredentials: true },
      );
      toast.success(`Enrolled ${data.added || 0} learner(s)${data.already_enrolled ? ` · ${data.already_enrolled} already in cohort` : ""}`);
      setShowHistorical(false);
      fetchRoster();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to enroll learners");
    } finally { setHistoricalEnrolling(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError("");
    if (!addForm.first_name.trim() || !addForm.last_name.trim() || !addForm.email.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/cohorts/${cohortId}/learners`, addForm, {
        withCredentials: true,
      });
      toast.success(`${label} added`);
      setShowAdd(false);
      setAddForm({ first_name: "", last_name: "", email: "" });
      fetchRoster();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setAddError(typeof detail === "string" ? detail : "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  // Debounced search against the global learner directory. Excludes
  // learners already enrolled in this cohort so admins don't accidentally
  // re-add someone who is already on the roster.
  useEffect(() => {
    if (!showAdd || addMode !== "search") return;
    const term = searchQ.trim();
    if (!term) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/learners`, {
          params: { search: term },
          withCredentials: true,
        });
        if (cancelled) return;
        const enrolledIds = new Set(learners.map((c) => c.id));
        setSearchResults(
          (data || []).filter((l) => !enrolledIds.has(l.id)).slice(0, 15),
        );
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [searchQ, addMode, showAdd, learners]);

  const enrollExisting = async (learner) => {
    setEnrollingId(learner.id);
    setAddError("");
    try {
      await axios.post(
        `${API}/cohorts/${cohortId}/learners`,
        {
          first_name: learner.first_name,
          last_name: learner.last_name,
          email: learner.email,
        },
        { withCredentials: true },
      );
      toast.success(`${learner.first_name} ${learner.last_name} enrolled`);
      setShowAdd(false);
      setSearchQ("");
      setSearchResults([]);
      fetchRoster();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setAddError(typeof detail === "string" ? detail : "Failed to enroll");
    } finally {
      setEnrollingId(null);
    }
  };

  const closeAddModal = (open) => {
    setShowAdd(open);
    if (!open) {
      setAddError("");
      setAddForm({ first_name: "", last_name: "", email: "" });
      setSearchQ("");
      setSearchResults([]);
      setAddMode("search");
    }
  };

  const handleRemove = async (learnerId, name) => {
    if (!window.confirm(`Remove ${name} from this cohort?`)) return;
    try {
      await axios.delete(`${API}/cohorts/${cohortId}/learners/${learnerId}`, {
        withCredentials: true,
      });
      toast.success(`${name} removed`);
      fetchRoster();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const handleConfirmWalkin = async (learnerId, name) => {
    try {
      await axios.post(
        `${API}/cohorts/${cohortId}/learners/${learnerId}/confirm-walkin`,
        {},
        { withCredentials: true }
      );
      toast.success(`${name} confirmed on roster`);
      fetchRoster();
    } catch {
      toast.error("Failed to confirm enrollment");
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    setCsvResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/cohorts/${cohortId}/learners/csv`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      setCsvResult(data);
      if (data.added > 0) {
        toast.success(`${data.added} ${label.toLowerCase()}${data.added !== 1 ? "s" : ""} imported`);
        fetchRoster();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "CSV import failed");
    } finally {
      setCsvUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const isArchived = cohortStatus !== "active";

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="roster-tab">
      {/* Actions */}
      {!isArchived && (
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setShowAdd(true)}
            data-testid="add-learner-btn"
            className="h-9 px-4 font-body text-sm font-medium text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add {label}
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowCsv(true)}
            data-testid="csv-import-btn"
            className="h-9 px-4 font-body text-sm font-medium border-white/10 text-[#A1A9B8] hover:text-white hover:bg-white/5"
          >
            <Upload className="h-4 w-4 mr-1.5" />
            CSV Import
          </Button>
          <Button
            variant="outline"
            onClick={openHistoricalOrphans}
            data-testid="historical-orphans-btn"
            className="h-9 px-4 font-body text-sm font-medium border-white/10 text-[#A1A9B8] hover:text-white hover:bg-white/5"
          >
            <ClipboardList className="h-4 w-4 mr-1.5" />
            Import from Historical Assessments
          </Button>
        </div>
      )}

      {/* Bulk action bar — appears when one or more learners are selected */}
      {selectedIds.size > 0 && !isArchived && (
        <div
          className="rounded-lg border border-[var(--primary-color,#4A90D9)]/40 bg-[var(--primary-color,#4A90D9)]/10 px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap"
          data-testid="roster-bulk-bar"
        >
          <div className="text-sm font-body text-white inline-flex items-center gap-2">
            <span className="font-semibold">{selectedIds.size}</span> {label.toLowerCase()}{selectedIds.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              className="text-[#A1A9B8] hover:text-white hover:bg-white/5 text-xs h-7 px-2"
              data-testid="roster-bulk-clear"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={openAssignModal}
              className="gap-1.5 h-8 px-3 text-sm font-medium text-white rounded-lg hover:opacity-90"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
              data-testid="roster-bulk-assign-event"
            >
              <Ticket className="h-3.5 w-3.5" /> Assign Event
            </Button>
          </div>
        </div>
      )}

      {/* Walk-in pending banner */}
      {learners.some((c) => c.is_walkin_pending) && (
        <div
          className="rounded-lg px-4 py-3 flex items-start gap-3 bg-amber-300 border border-amber-400"
          data-testid="walkin-pending-banner"
        >
          <AlertCircle className="h-4 w-4 text-amber-900 shrink-0 mt-0.5" />
          <div className="text-sm font-body text-amber-950">
            <strong className="text-amber-950 font-bold">
              {learners.filter((c) => c.is_walkin_pending).length} walk-in{learners.filter((c) => c.is_walkin_pending).length !== 1 ? "s" : ""}
            </strong>{" "}
            added from the check-in page. Review each entry and click <em>Confirm</em> to officially add them to the roster.
          </div>
        </div>
      )}

      {/* Roster list */}
      {learners.length === 0 ? (
        <div
          className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center"
          data-testid="roster-empty-state"
        >
          <div
            className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 10%, transparent)" }}
          >
            <Users className="h-7 w-7" style={{ color: "var(--primary-color, #4A90D9)" }} />
          </div>
          <h3 className="font-body text-lg font-bold text-white mb-2">
            No {label.toLowerCase()}s enrolled yet
          </h3>
          <p className="text-[#6E7787] font-body text-sm max-w-sm mx-auto">
            Add {label.toLowerCase()}s individually or import via CSV.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="roster-list">
          <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-2 text-xs text-[#6E7787] font-body uppercase tracking-wider items-center">
            <div className="col-span-1 flex items-center">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleSelectAll}
                disabled={isArchived}
                className="h-5 w-5 border-2 border-slate-500 bg-white data-[state=checked]:bg-[var(--primary-color,#4A90D9)] data-[state=checked]:border-[var(--primary-color,#4A90D9)] data-[state=checked]:text-white shadow-sm"
                data-testid="roster-select-all"
              />
            </div>
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-3">Passports</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1"></div>
          </div>
          {learners.map((c) => {
            const isSelected = selectedIds.has(c.id);
            const assignments = c.passport_assignments || [];
            return (
            <div
              key={c.id}
              className={`bg-[#11141A] border rounded-lg px-4 py-3 hover:border-white/20 transition-all ${
                isSelected
                  ? "border-[var(--primary-color,#4A90D9)] ring-1 ring-[var(--primary-color,#4A90D9)]/30"
                  : c.is_walkin_pending
                  ? "border-l-2 border-white/10 border-l-amber-400"
                  : "border-white/10"
              }`}
              data-testid={`roster-row-${c.id}`}
            >
              <div className="sm:grid sm:grid-cols-12 sm:gap-4 sm:items-center">
                <div className="col-span-1 flex items-center">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleLearnerSelected(c.id)}
                    disabled={isArchived}
                    className="h-5 w-5 border-2 border-slate-500 bg-white data-[state=checked]:bg-[var(--primary-color,#4A90D9)] data-[state=checked]:border-[var(--primary-color,#4A90D9)] data-[state=checked]:text-white shadow-sm"
                    data-testid={`roster-select-${c.id}`}
                  />
                </div>
                <div className="col-span-3 text-sm font-body text-white font-medium flex items-center gap-2 flex-wrap" data-testid={`roster-name-${c.id}`}>
                  <span>{c.first_name} {c.last_name}</span>
                  {c.is_walkin_pending && (
                    <Badge
                      className="bg-amber-300 text-amber-950 border-amber-400 text-[10px] uppercase tracking-wider font-semibold"
                      data-testid={`walkin-pending-badge-${c.id}`}
                    >
                      Walk-in · needs confirmation
                    </Badge>
                  )}
                </div>
                <div className="col-span-3 text-sm font-body text-[#A1A9B8] mt-1 sm:mt-0 truncate" data-testid={`roster-email-${c.id}`}>
                  {c.email}
                </div>
                <div className="col-span-3 mt-1 sm:mt-0 flex flex-wrap gap-1.5" data-testid={`roster-passports-${c.id}`}>
                  {assignments.length === 0 ? (
                    <span className="text-xs text-[#6E7787] font-body">—</span>
                  ) : (
                    assignments.map((a) => (
                      <span
                        key={`${a.event_id}-${a.passport_id}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-black/30 px-2 py-1 text-[11px] font-body"
                        data-testid={`roster-passport-badge-${c.id}-${a.passport_id}`}
                      >
                        <Ticket className="h-3 w-3 text-[var(--primary-color,#4A90D9)]" />
                        <span className="text-white truncate max-w-[110px]">{a.passport_name || "Passport"}</span>
                        <span className="text-[#6E7787]">·</span>
                        <span className="text-[#A1A9B8] truncate max-w-[110px]">{a.event_name || "Event"}</span>
                      </span>
                    ))
                  )}
                </div>
                <div className="col-span-1 mt-1 sm:mt-0">
                  <Badge
                    className={
                      c.status === "active"
                        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                        : "bg-[#6E7787]/15 text-[#6E7787] border-[#6E7787]/20"
                    }
                  >
                    {c.status}
                  </Badge>
                </div>
                <div className="col-span-1 mt-2 sm:mt-0 flex justify-end gap-1 flex-wrap">
                  {c.is_walkin_pending && !isArchived && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleConfirmWalkin(c.id, `${c.first_name} ${c.last_name}`)}
                      data-testid={`confirm-walkin-${c.id}`}
                      className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 text-xs h-7 px-2"
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1" />
                      Confirm
                    </Button>
                  )}
                  {!isArchived && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(c.id, `${c.first_name} ${c.last_name}`)}
                      data-testid={`remove-learner-${c.id}`}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs h-7 px-2"
                    >
                      <X className="h-3.5 w-3.5 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
          <p className="text-xs text-[#6E7787] font-body pt-2">
            {learners.length} {label.toLowerCase()}{learners.length !== 1 ? "s" : ""} enrolled
          </p>
        </div>
      )}

      {/* Add Individual Modal */}
      <Dialog open={showAdd} onOpenChange={closeAddModal}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-md" data-testid="add-learner-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">Add {label}</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">
              {addMode === "search"
                ? `Search the ${label.toLowerCase()} directory and enroll an existing ${label.toLowerCase()} in this cohort.`
                : `Enter the ${label.toLowerCase()}'s information to enroll them in this cohort.`}
            </DialogDescription>
          </DialogHeader>

          {/* Mode toggle */}
          <div className="inline-flex bg-[#0A0C10] border border-white/10 rounded-lg p-1 gap-0.5" data-testid="add-learner-mode">
            <button
              type="button"
              onClick={() => { setAddMode("search"); setAddError(""); }}
              data-testid="add-learner-mode-search"
              className={`flex-1 px-3 py-1.5 text-sm font-body rounded-md transition-colors ${
                addMode === "search"
                  ? "bg-white/10 text-white"
                  : "text-[#A1A9B8] hover:text-white"
              }`}
            >
              <Search className="h-3.5 w-3.5 inline mr-1.5" />
              Search existing
            </button>
            <button
              type="button"
              onClick={() => { setAddMode("create"); setAddError(""); }}
              data-testid="add-learner-mode-create"
              className={`flex-1 px-3 py-1.5 text-sm font-body rounded-md transition-colors ${
                addMode === "create"
                  ? "bg-white/10 text-white"
                  : "text-[#A1A9B8] hover:text-white"
              }`}
            >
              <UserPlus className="h-3.5 w-3.5 inline mr-1.5" />
              Create new
            </button>
          </div>

          {addMode === "search" ? (
            <div className="space-y-3 mt-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787] pointer-events-none" />
                <Input
                  autoFocus
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder={`Search ${label.toLowerCase()}s by name or email…`}
                  data-testid="add-learner-search-input"
                  className="pl-9 bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10"
                />
              </div>
              <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1" data-testid="add-learner-search-results">
                {!searchQ.trim() ? (
                  <p className="text-sm text-[#6E7787] font-body text-center py-6">
                    Start typing to search the {label.toLowerCase()} directory.
                  </p>
                ) : searching ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-4 w-4 animate-spin text-[#6E7787]" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <p className="text-sm text-[#6E7787] font-body text-center py-6">
                    No matches found. Try a different search or use "Create new".
                  </p>
                ) : (
                  searchResults.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => enrollExisting(l)}
                      disabled={!!enrollingId}
                      data-testid={`add-learner-search-result-${l.id}`}
                      className="w-full flex items-center justify-between gap-3 text-left px-3 py-2.5 rounded-lg border border-white/10 bg-[#0A0C10] hover:border-white/20 hover:bg-white/[0.02] transition-colors disabled:opacity-50"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-body font-medium text-white truncate">
                          {l.first_name} {l.last_name}
                        </div>
                        <div className="text-xs text-[#6E7787] font-body truncate">{l.email}</div>
                        {l.cohort_name && (
                          <div className="text-[10px] text-[#6E7787] font-body mt-0.5 uppercase tracking-wide">
                            Also in: {l.cohort_name}
                          </div>
                        )}
                      </div>
                      {enrollingId === l.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-[#6E7787] shrink-0" />
                      ) : (
                        <Plus className="h-4 w-4 text-[var(--primary-color,#4A90D9)] shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
              {addError && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="add-learner-error">
                  <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
                  <p className="text-red-400 text-sm font-body">{addError}</p>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleAdd} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[#A1A9B8] font-body text-sm">First Name <span className="text-red-400">*</span></Label>
                  <Input
                    value={addForm.first_name}
                    onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })}
                    required
                    data-testid="add-learner-first-name"
                    className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#A1A9B8] font-body text-sm">Last Name <span className="text-red-400">*</span></Label>
                  <Input
                    value={addForm.last_name}
                    onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })}
                    required
                    data-testid="add-learner-last-name"
                    className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">Email <span className="text-red-400">*</span></Label>
                <Input
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                  required
                  data-testid="add-learner-email"
                  className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10"
                />
              </div>
              {addError && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="add-learner-error">
                  <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
                  <p className="text-red-400 text-sm font-body">{addError}</p>
                </div>
              )}
              <DialogFooter className="pt-2">
                <Button type="button" variant="ghost" onClick={() => closeAddModal(false)} className="text-[#A1A9B8] hover:text-white hover:bg-white/5">Cancel</Button>
                <Button
                  type="submit"
                  disabled={adding}
                  data-testid="add-learner-save"
                  className="font-body font-medium text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
                >
                  {adding ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Adding...</> : `Add ${label}`}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <Dialog open={showCsv} onOpenChange={(open) => { setShowCsv(open); if (!open) setCsvResult(null); }}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-lg" data-testid="csv-import-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">CSV Import</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">
              Upload a CSV file with columns: <span className="text-[#A1A9B8] font-mono text-xs">first_name, last_name, email</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="border border-dashed border-white/10 rounded-lg p-6 text-center">
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                className="hidden"
                data-testid="csv-file-input"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer inline-flex flex-col items-center gap-2"
              >
                <Upload className="h-8 w-8 text-[#6E7787]" />
                <span className="text-sm text-[#A1A9B8] font-body">
                  {csvUploading ? "Uploading..." : "Click to select CSV file"}
                </span>
              </label>
            </div>

            {csvResult && (
              <div className="space-y-3" data-testid="csv-result">
                <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                  <p className="text-emerald-400 text-sm font-body">
                    {csvResult.added} of {csvResult.total_rows} {label.toLowerCase()}s imported successfully
                  </p>
                </div>
                {csvResult.errors?.length > 0 && (
                  <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <p className="text-red-400 text-sm font-body font-medium mb-2">Errors:</p>
                    {csvResult.errors.map((err, i) => (
                      <p key={i} className="text-red-400/80 text-xs font-body">
                        Row {err.row}: {err.email ? `${err.email} — ` : ""}{err.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Event modal — picks one passport across all events */}
      <Dialog open={assignOpen} onOpenChange={(v) => { if (!assigning) setAssignOpen(v); }}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-2xl" data-testid="assign-event-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">Assign Events</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">
              Check the events you want to assign to {selectedIds.size} selected {label.toLowerCase()}{selectedIds.size === 1 ? "" : "s"}.
              {selectedIds.size === 1
                ? " Uncheck an event to remove the existing assignment. A learner can only hold one passport per event."
                : " Existing passports on the same event will be replaced."}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 max-h-[55vh] overflow-y-auto pr-1 space-y-3" data-testid="assign-event-options">
            {assignOptionsLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#6E7787]" /></div>
            ) : assignOptions.length === 0 ? (
              <p className="text-sm text-[#6E7787] font-body text-center py-10">
                No passports available. Create an event with passports first.
              </p>
            ) : (
              assignOptions.map((ev) => {
                const draft = assignDrafts[ev.event_id] || { checked: false, passport_id: null };
                const initial = initialAssignDrafts[ev.event_id] || { checked: false, passport_id: null };
                const willUnassign = !draft.checked && initial.checked && selectedIds.size === 1;
                return (
                  <div
                    key={ev.event_id}
                    className={`rounded-lg border transition-colors ${
                      draft.checked
                        ? "border-[var(--primary-color,#4A90D9)]/60 bg-[var(--primary-color,#4A90D9)]/5"
                        : willUnassign
                        ? "border-red-400/40 bg-red-500/5"
                        : "border-white/10"
                    }`}
                    data-testid={`assign-event-group-${ev.event_id}`}
                  >
                    <label
                      className="flex items-start gap-3 px-3 py-2.5 cursor-pointer select-none border-b border-white/10"
                      data-testid={`assign-event-toggle-${ev.event_id}`}
                    >
                      <Checkbox
                        checked={draft.checked}
                        onCheckedChange={() => toggleAssignEvent(ev.event_id)}
                        className="mt-0.5 h-5 w-5 border-2 border-slate-500 bg-white data-[state=checked]:bg-[var(--primary-color,#4A90D9)] data-[state=checked]:border-[var(--primary-color,#4A90D9)] data-[state=checked]:text-white shadow-sm"
                        data-testid={`assign-event-checkbox-${ev.event_id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-body font-semibold text-white inline-flex items-center gap-2 flex-wrap">
                          <Calendar className="h-3.5 w-3.5 text-[#6E7787]" /> {ev.event_name}
                          {willUnassign ? (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 font-semibold">
                              Will unassign
                            </span>
                          ) : null}
                          {initial.checked && draft.checked && selectedIds.size === 1 ? (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-semibold">
                              Currently assigned
                            </span>
                          ) : null}
                        </p>
                        <p className="text-[11px] font-body text-[#6E7787] mt-0.5 inline-flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {ev.event_date}</span>
                          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {ev.event_start_time}</span>
                          {ev.event_location ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.event_location}</span> : null}
                        </p>
                      </div>
                    </label>
                    {draft.checked ? (
                      <div className="p-2 space-y-1.5">
                        {ev.passports.length === 0 ? (
                          <p className="text-xs font-body text-[#6E7787] px-3 py-2">No passports configured for this event.</p>
                        ) : (
                          ev.passports.map((p) => {
                            const selected = draft.passport_id === p.id;
                            return (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => pickAssignPassport(ev.event_id, p.id)}
                                className={`w-full flex items-center justify-between gap-3 text-left px-3 py-2.5 rounded-md border transition-colors ${
                                  selected
                                    ? "border-[var(--primary-color,#4A90D9)] bg-[var(--primary-color,#4A90D9)]/10"
                                    : "border-white/10 bg-black/20 hover:border-white/20"
                                }`}
                                data-testid={`assign-passport-${p.id}`}
                              >
                                <div className="min-w-0 inline-flex items-center gap-2">
                                  <Ticket className={`h-3.5 w-3.5 shrink-0 ${selected ? "text-[var(--primary-color,#4A90D9)]" : "text-[#6E7787]"}`} />
                                  <span className="text-sm font-body text-white truncate">{p.name}</span>
                                </div>
                                <span className="text-[11px] font-body text-[#6E7787] shrink-0">
                                  {(p.assignments || []).length} {(p.assignments || []).length === 1 ? "session" : "sessions"}
                                </span>
                              </button>
                            );
                          })
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setAssignOpen(false)}
              disabled={assigning}
              className="text-[#A1A9B8] hover:text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={assigning}
              data-testid="assign-passport-confirm"
              className="font-body font-medium text-white rounded-lg hover:opacity-90"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
            >
              {assigning ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Saving...</> : "Save Assignments"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from Historical Assessments modal — lists learners referenced
          in any historical assessment CSV import for this cohort who are not
          currently in the roster. Admin picks who to enroll. */}
      <Dialog open={showHistorical} onOpenChange={(v) => { if (!historicalEnrolling) setShowHistorical(v); }}>
        <DialogContent className="max-w-2xl bg-[#0F1216] border-white/10 text-white" data-testid="historical-orphans-modal">
          <DialogHeader>
            <DialogTitle className="text-white font-heading inline-flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-[var(--primary-color)]" /> Import from Historical Assessments
            </DialogTitle>
            <DialogDescription className="text-[#A1A9B8] font-body">
              {historicalLoading
                ? "Scanning historical assessment imports…"
                : historicalOrphans.length === 0
                  ? "No orphaned learners found. Every learner referenced in past historical assessment imports for this cohort is already on the roster."
                  : `${historicalOrphans.length} learner${historicalOrphans.length === 1 ? "" : "s"} referenced by past imports are not on this roster. Select who to enroll.`}
            </DialogDescription>
          </DialogHeader>
          {historicalLoading ? (
            <div className="py-8 flex items-center justify-center text-[#6E7787]">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : historicalOrphans.length > 0 ? (
            <div className="max-h-80 overflow-y-auto border border-white/10 rounded-md bg-[#0A0C10]">
              <div className="sticky top-0 bg-[#0A0C10] border-b border-white/10 px-3 py-2 flex items-center gap-2">
                <Checkbox
                  checked={historicalSelected.size === historicalOrphans.length}
                  onCheckedChange={toggleAllOrphans}
                  className="h-4 w-4 border-2 border-white/60 bg-[#0A0C10] data-[state=checked]:bg-[var(--primary-color)] data-[state=checked]:border-[var(--primary-color)]"
                  data-testid="historical-orphans-select-all"
                />
                <span className="text-[10px] uppercase tracking-wider font-body font-semibold text-[#A1A9B8]">
                  {historicalSelected.size} of {historicalOrphans.length} selected
                </span>
              </div>
              <ul className="divide-y divide-white/5">
                {historicalOrphans.map((o) => (
                  <li
                    key={o.email}
                    className="px-3 py-2 flex items-center gap-3 hover:bg-white/[0.02]"
                    data-testid={`historical-orphan-row-${o.email}`}
                  >
                    <Checkbox
                      checked={historicalSelected.has(o.email)}
                      onCheckedChange={() => toggleOrphan(o.email)}
                      className="h-4 w-4 border-2 border-white/60 bg-[#0A0C10] data-[state=checked]:bg-[var(--primary-color)] data-[state=checked]:border-[var(--primary-color)] shrink-0"
                      data-testid={`historical-orphan-select-${o.email}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-body text-white truncate">
                        {((o.first_name || "") + " " + (o.last_name || "")).trim() || "(no name)"}
                      </p>
                      <p className="text-xs font-body text-[#6E7787] truncate">{o.email}</p>
                    </div>
                    {o.assessments && o.assessments.length > 0 ? (
                      <span className="text-[10px] font-body text-[#A1A9B8] truncate max-w-[240px]" title={o.assessments.join(", ")}>
                        {o.assessments.join(", ")}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <DialogFooter className="gap-3 sm:gap-3">
            <Button
              variant="outline"
              onClick={() => setShowHistorical(false)}
              disabled={historicalEnrolling}
              className="border-white/15 text-white bg-transparent hover:bg-white/5"
              data-testid="historical-orphans-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmEnrollOrphans}
              disabled={historicalEnrolling || historicalLoading || historicalOrphans.length === 0 || historicalSelected.size === 0}
              style={{ background: "var(--primary-color)", color: "var(--admin-button-fg, #fff)" }}
              data-testid="historical-orphans-confirm"
            >
              {historicalEnrolling ? <Loader2 className="h-4 w-4 animate-spin" /> : `Enroll ${historicalSelected.size} learner${historicalSelected.size === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
